import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Download, Eye, X, FileText, Calculator, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Employee360FamilyPensionCard from './Employee360FamilyPensionCard';
import Employee360CertificateCard from './Employee360CertificateCard';
import Employee360OfficialNoteCard from './Employee360OfficialNoteCard';
import Employee360PensionScrollCard from './Employee360PensionScrollCard';
import Employee360PensionApplicationForm from './Employee360PensionApplicationForm';

const Employee360PensionDocs = ({ employeeId }) => {
  const { hasPermission } = useAuth();
  const [profile, setProfile] = useState(null);
  const [qualifyingService, setQualifyingService] = useState(null);
  const [pensionReport, setPensionReport] = useState(null);
  const [capitalizedPensionReport, setCapitalizedPensionReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [showCalcPreview, setShowCalcPreview] = useState(false);
  const [showPensionReportPreview, setShowPensionReportPreview] = useState(false);
  const [showPensionAppForm, setShowPensionAppForm] = useState(false);

  const printRef = useRef(null);
  const printRefCalc = useRef(null);
  const printRefPensionReport = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, qsRes, prRes, cprRes] = await Promise.all([
          axios.get(`/v1/employee-master/${employeeId}/profile-360`),
          axios.get(`/v1/employee-master/${employeeId}/qualifying-service`),
          axios.get(`/v1/employee-master/${employeeId}/pension-report`).catch(err => {
            console.error("No pension report or data setup", err);
            return { data: null };
          }),
          axios.get(`/v1/employee-master/${employeeId}/capitalized-pension-report`).catch(err => {
            console.error("No capitalized pension report or data setup", err);
            return { data: null };
          })
        ]);
        setProfile(profileRes.data);
        setQualifyingService(qsRes.data);
        setPensionReport(prRes.data);
        setCapitalizedPensionReport(cprRes.data);
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [employeeId]);

  // Component-level permission check
  if (!hasPermission('emp:pensiondoc:view') && !hasPermission('*')) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-center space-x-2">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <span>You do not have permission to view pension documents.</span>
      </div>
    );
  }

  const loadHtml2Pdf = async () => {
    if (!window.html2pdf) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
  };

  const handleDownloadPDF = async () => {
    const printContent = printRef.current;
    if (!printContent) return;

    await loadHtml2Pdf();

    // Create an isolated PDF container so modal/scroll/flex styles
    // do not affect the downloaded Qualifying Service PDF.
    const pdfContainer = document.createElement('div');

    pdfContainer.style.position = 'absolute';
    pdfContainer.style.left = '-99999px';
    pdfContainer.style.top = '0';
    pdfContainer.style.width = '794px';
    pdfContainer.style.margin = '0';
    pdfContainer.style.padding = '0';
    pdfContainer.style.background = '#ffffff';
    pdfContainer.style.boxSizing = 'border-box';

    const pdfContent = printContent.cloneNode(true);

    // Explicitly remove any possible positioning/margins/padding
    // inherited from the preview layout.
    pdfContent.style.position = 'static';
    pdfContent.style.margin = '0';
    pdfContent.style.paddingTop = '0';
    pdfContent.style.paddingBottom = '0';
    pdfContent.style.paddingLeft = '0';
    pdfContent.style.paddingRight = '0';
    pdfContent.style.width = '100%';
    pdfContent.style.maxWidth = 'none';
    pdfContent.style.minHeight = '0';
    pdfContent.style.height = 'auto';
    pdfContent.style.background = '#ffffff';

    pdfContainer.appendChild(pdfContent);
    document.body.appendChild(pdfContainer);

    // Give the browser one rendering cycle before html2canvas captures it.
    await new Promise(resolve => requestAnimationFrame(resolve));

    const opt = {
      margin: [5, 10, 10, 10],
      filename: `Qualifying_Service_${employeeId}.pdf`,
      image: {
        type: 'jpeg',
        quality: 0.98
      },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait'
      },
      pagebreak: {
        mode: ['css', 'legacy']
      }
    };

    try {
      await window
        .html2pdf()
        .set(opt)
        .from(pdfContent)
        .save();
    } finally {
      document.body.removeChild(pdfContainer);
    }
  };

  const handleDownloadCalcPDF = async () => {
    const printContent = printRefCalc.current;
    if (!printContent) return;

    await loadHtml2Pdf();

    // Create an isolated container specifically for the Calculation Sheet PDF.
    // This prevents modal, flex, overflow, and preview styles from affecting
    // the downloaded PDF.
    const pdfContainer = document.createElement('div');

    pdfContainer.style.position = 'absolute';
    pdfContainer.style.left = '-99999px';
    pdfContainer.style.top = '0';
    pdfContainer.style.width = '794px';
    pdfContainer.style.margin = '0';
    pdfContainer.style.padding = '0';
    pdfContainer.style.background = '#ffffff';
    pdfContainer.style.boxSizing = 'border-box';

    const pdfContent = printContent.cloneNode(true);

    // Reset layout-related styles for PDF rendering.
    pdfContent.style.position = 'static';
    pdfContent.style.margin = '0';
    pdfContent.style.paddingTop = '0';
    pdfContent.style.paddingBottom = '0';
    pdfContent.style.paddingLeft = '0';
    pdfContent.style.paddingRight = '0';
    pdfContent.style.width = '100%';
    pdfContent.style.maxWidth = 'none';
    pdfContent.style.minHeight = '0';
    pdfContent.style.height = 'auto';
    pdfContent.style.background = '#ffffff';

    pdfContainer.appendChild(pdfContent);
    document.body.appendChild(pdfContainer);

    // Allow the browser to finish rendering the cloned content.
    await new Promise(resolve => requestAnimationFrame(resolve));

    const opt = {
      // Small intentional top margin.
      margin: [5, 10, 10, 10],

      filename: `Calculation_Sheet_${employeeId}.pdf`,

      image: {
        type: 'jpeg',
        quality: 0.98
      },

      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0
      },

      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait'
      },

      pagebreak: {
        mode: ['css', 'legacy']
      }
    };

    try {
      await window
        .html2pdf()
        .set(opt)
        .from(pdfContent)
        .save();
    } finally {
      // Remove the temporary PDF-only DOM after generation.
      document.body.removeChild(pdfContainer);
    }
  };

  const handleDownloadPensionReportPDF = async () => {
    const printContent = printRefPensionReport.current;
    if (!printContent) return;

    await loadHtml2Pdf();

    const opt = {
      margin: [0, 15, 15, 15], // remove PDF top margin only
      filename: `Provisional_Gratuity_${employeeId}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        scrollY: 0,
        scrollX: 0
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        hotfixes: ['px_scaling']
      },
      pagebreak: {
        mode: ['avoid-all', 'css', 'legacy']
      }
    };

    window.html2pdf()
      .set(opt)
      .from(printContent)
      .save();
  };

  if (loading) {
    return <div className="p-6 text-gray-500">Loading qualifying service data...</div>;
  }

  const personal = profile?.personal || {};
  const employment = profile?.employment || {};

  // Use backend qualifying service data
  const name = qualifyingService?.name || personal.full_name || `${personal.first_name || ''} ${personal.last_name || ''}`.trim() || 'N/A';
  const designation = qualifyingService?.designation || employment.designation || 'N/A';

  const dor = qualifyingService?.date_of_relief ? new Date(qualifyingService.date_of_relief) : null;
  const doa = qualifyingService?.date_of_appointment ? new Date(qualifyingService.date_of_appointment) : null;

  const totalService = qualifyingService?.total_service || { years: '', months: '', days: '' };
  const eol = qualifyingService?.eol || { total_days: 0, years: 0, months: 0, days: 0 };
  const totalNetService = qualifyingService?.total_net_service || { years: '', months: '', days: '', decimal_years: 0 };
  const netQualifyingService = qualifyingService?.net_qualifying_service || 0;

  // Extract date components for display
  const rY = dor?.getFullYear() || '';
  const rM = dor?.getMonth() + 1 || '';
  const rD = dor?.getDate() || '';

  const jY = doa?.getFullYear() || '';
  const jM = doa?.getMonth() + 1 || '';
  const jD = doa?.getDate() || '';

  const diffY = totalService.years;
  const diffM = totalService.months;
  const diffD = totalService.days;

  const netY = totalNetService.years;
  const netM = totalNetService.months;
  const netD = totalNetService.days;

  const eolY = eol.years;
  const eolM = eol.months;
  const eolD = eol.days;

  const dobDate = personal.date_of_birth ? new Date(personal.date_of_birth) : null;
  const formatDateDot = (d) => {
    if (!d || isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${d.getFullYear()}`;
  };

  // Use backend capitalized pension report data
  const cpr = capitalizedPensionReport;
  const nextDobDate = cpr?.next_birthday ? new Date(cpr.next_birthday) : null;

  const basicPayRaw = cpr?.last_basic_pay || profile?.employment?.basic_pay || profile?.payroll?.basic_pay || profile?.basic_pay || 69900;
  const pensionSanctioned = cpr?.pension_sanctioned || Math.round(basicPayRaw / 2);
  const commutedValueAmount = cpr?.pension_proposed_to_be_commuted || Math.round(pensionSanctioned * 0.4);
  const residuaryPension = cpr?.residuary_pension || (pensionSanctioned - commutedValueAmount);
  const ageFactor = cpr?.commutation_factor || 8.194;
  const rawCommutatedValue = cpr?.raw_commuted_value || (commutedValueAmount * ageFactor * 12);
  const roundedCommutatedValue = cpr?.commuted_value || Math.ceil(rawCommutatedValue);

  const formatCurrency = (val) => Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatCurrencyInt = (val) => Number(val).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-green-50 text-green-600 rounded-lg">
            <FileText size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Provisional Gratuity</h3>
            <p className="text-sm text-gray-500">View and download the provisional gratuity report.</p>
          </div>
        </div>
        <button
          onClick={() => setShowPensionReportPreview(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 text-gray-700 rounded-md hover:bg-gray-100 font-medium transition-colors"
        >
          <Eye size={18} />
          Preview
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
            <FileText size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Qualifying Service</h3>
            <p className="text-sm text-gray-500">View and download the qualifying service calculation report.</p>
          </div>
        </div>
        <button
          onClick={() => setShowPreview(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 text-gray-700 rounded-md hover:bg-gray-100 font-medium transition-colors"
        >
          <Eye size={18} />
          Preview
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg">
            <Calculator size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Calculation Sheet</h3>
            <p className="text-sm text-gray-500">View and download the calculation sheet of capitalized value of pension.</p>
          </div>
        </div>
        <button
          onClick={() => setShowCalcPreview(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 text-gray-700 rounded-md hover:bg-gray-100 font-medium transition-colors"
        >
          <Eye size={18} />
          Preview
        </button>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-2">
          <div className="bg-gray-50 rounded-xl shadow-2xl w-[1000px] max-w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-800">Qualifying Service Preview</h2>
              <div className="flex gap-3 items-center">
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors"
                >
                  <Download size={16} />
                  Download PDF
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-md text-gray-500 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-2 overflow-y-auto flex-1">
              <div className="bg-white rounded-lg border border-gray-200 p-2 shadow-sm overflow-x-auto">
                <div ref={printRef} className="max-w-4xl mx-auto w-full pt-0 text-black bg-white">
                  <div className="text-center mb-6">
                    <div className="font-bold text-lg mb-1">पुणे छावनी परिषद</div>
                    <div className="font-bold text-xl mb-1 tracking-wide">PUNE CANTONMENT BOARD</div>
                    <div className="font-bold text-lg mb-1">गोलीबार मैदान, पुणे - 1.</div>
                    <div className="font-bold text-lg mb-2 tracking-wide">GOLIBAR MAIDAN, PUNE - 1</div>
                    <div className="font-bold text-xl uppercase mt-6 tracking-wide">QUALIFYING SERVICE</div>
                  </div>
                  <div className="space-y-6 text-[15px] font-serif">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-1">1</div>
                      <div className="col-span-4">
                        <div className="flex justify-between w-4/5">
                          <span>नाम</span>
                          <span>Name</span>
                        </div>
                      </div>
                      <div className="col-span-7 font-bold uppercase tracking-wide">{name}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-1">2</div>
                      <div className="col-span-4">
                        <div className="flex justify-between w-4/5">
                          <span>पदनाम</span>
                          <span>Designation</span>
                        </div>
                      </div>
                      <div className="col-span-7 font-bold uppercase tracking-wide">{designation}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-8">
                      <div className="col-span-5"></div>
                      <div className="col-span-2 text-center font-bold">Y</div>
                      <div className="col-span-2 text-center font-bold">M</div>
                      <div className="col-span-2 text-center font-bold">D</div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 items-end mt-2">
                      <div className="col-span-1">3</div>
                      <div className="col-span-4">
                        <div>निवृत्ती की तारीख</div>
                        <div>Date of relieve from</div>
                        <div>Cantonement Board, Pune</div>
                        <div className="font-bold mt-1">RETIRED</div>
                      </div>
                      <div className="col-span-2 text-center pb-1">{rY}</div>
                      <div className="col-span-2 text-center pb-1">{rM}</div>
                      <div className="col-span-2 text-center pb-1">{rD}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 items-end mt-6">
                      <div className="col-span-1">4</div>
                      <div className="col-span-4">
                        <div>नियुक्ती कि तारीख</div>
                        <div>Date of Appointment</div>
                      </div>
                      <div className="col-span-2 text-center border-b border-black pb-2">{jY}</div>
                      <div className="col-span-2 text-center border-b border-black pb-2">{jM}</div>
                      <div className="col-span-2 text-center border-b border-black pb-2">{jD}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 items-end mt-6">
                      <div className="col-span-1">5</div>
                      <div className="col-span-4">
                        <div>पूरा किया हुआ कार्यकाल</div>
                        <div>Total Service</div>
                      </div>
                      <div className="col-span-2 text-center pb-1">{diffY}</div>
                      <div className="col-span-2 text-center pb-1">{diffM}</div>
                      <div className="col-span-2 text-center pb-1">{diffD}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 items-end mt-6">
                      <div className="col-span-1">6</div>
                      <div className="col-span-4">
                        <div>असाधारण छुट्टी</div>
                        <div>EOL (days)</div>
                      </div>
                      <div className="col-span-2 text-center border-b border-black pb-6">{eolY}</div>
                      <div className="col-span-2 text-center border-b border-black pb-6">{eolM}</div>
                      <div className="col-span-2 text-center border-b border-black pb-6">{eolD}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-3 items-end">
                      <div className="col-span-1"></div>
                      <div className="col-span-4">
                        <div className="font-bold"></div>
                      </div>
                      <div className="col-span-2 text-center font-bold">{netY}</div>
                      <div className="col-span-2 text-center font-bold">{netM}</div>
                      <div className="col-span-2 text-center font-bold">{netD}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-4 items-center">
                      <div className="col-span-1"></div>
                      <div className="col-span-4"><div className="font-bold">Total Net Service</div></div>
                      <div className="col-span-7"><span className="font-bold text-lg">{totalNetService?.decimal_years ? `${totalNetService.decimal_years} Yrs.` : ''}</span></div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-4 items-center">
                      <div className="col-span-1"></div>
                      <div className="col-span-4"><div className="font-bold">Net qualifying service</div></div>
                      <div className="col-span-7"><span className="font-bold text-lg">{netQualifyingService ? `${netQualifyingService} Yrs.` : ''}</span></div>
                    </div>
                  </div>
                  <div className="mt-16 flex justify-between items-end px-4">
                    <div><div className="font-bold">वरिष्ठ लिपीक</div></div>
                    <div className="text-center mr-24">
                      <div className="font-bold mb-1">मुख्य लेखापाल</div>
                      <div className="font-bold text-sm tracking-wide">CHIEF ACCOUNTANT</div>
                    </div>
                  </div>
                  <div className="mt-12 flex justify-end px-4">
                    <div className="text-right">
                      <div className="font-bold text-sm tracking-wide mb-1">CHIEF EXECUTIVE OFFICER</div>
                      <div className="font-bold text-sm tracking-wide">PUNE CANTONMENT BOARD</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCalcPreview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-gray-50 rounded-xl shadow-2xl w-[1000px] max-w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-800">Calculation Sheet Preview</h2>
              <div className="flex gap-3 items-center">
                <button
                  onClick={handleDownloadCalcPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors"
                >
                  <Download size={16} />
                  Download PDF
                </button>
                <button
                  onClick={() => setShowCalcPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-md text-gray-500 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm overflow-x-auto">
                <div ref={printRefCalc} className="max-w-4xl mx-auto w-full text-black bg-white">
                  <div className="text-center mb-6">
                    <div className="font-bold text-xl mb-1 tracking-wide">PUNE CANTONMENT BOARD</div>
                    <div className="font-bold text-sm mb-1 tracking-wide">GOLIBAR MAIDAN, PUNE - 1</div>
                    <div className="font-bold text-[17px] mb-1 uppercase tracking-wide">CALCULATION SHEET OF CAPITALIZED VALUE OF PENSION</div>
                    <div className="text-[17px] mb-1">Calculation of Commutation of Pension without</div>
                    <div className="text-[17px]">Medical Examination / after Medical Examination</div>
                  </div>
                  <div className="space-y-4 text-[15px] font-serif px-8">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-1">1</div>
                      <div className="col-span-6">Name of Retired Employee</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4 font-bold uppercase">{name}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-1">2</div>
                      <div className="col-span-6">Designation</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4 uppercase">{designation}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-1">3</div>
                      <div className="col-span-6">Date of Birth</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4">{formatDateDot(dobDate)}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-1">4</div>
                      <div className="col-span-6">Date of relieve from the service</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4">{formatDateDot(dor)}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-end mt-4">
                      <div className="col-span-1">5</div>
                      <div className="col-span-6">Class of Pension</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4">SUPERANNUATION</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-1">6</div>
                      <div className="col-span-6">Amount of Pension Sanctioned</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4">{formatCurrency(pensionSanctioned)}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-1">7</div>
                      <div className="col-span-6">
                        Amount of Pension proposed to be<br />
                        <span className="ml-8">Commuted (40% of {pensionSanctioned})</span>
                      </div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4">{formatCurrencyInt(commutedValueAmount)}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-center mt-4">
                      <div className="col-span-1">8</div>
                      <div className="col-span-6">Residuary Pension</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4">{formatCurrencyInt(residuaryPension)}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-center mt-4">
                      <div className="col-span-1">9</div>
                      <div className="col-span-6">Date of Next Birth Day</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4">{formatDateDot(nextDobDate)}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2 mt-4">
                      <div className="col-span-1">10</div>
                      <div className="col-span-6">Commutated Value</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4">
                        <span>= {formatCurrencyInt(commutedValueAmount)} X {ageFactor} X 12</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-1"></div>
                      <div className="col-span-6 text-right">i.e. =</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4">{formatCurrency(rawCommutatedValue)}</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-1"></div>
                      <div className="col-span-6 text-right">i.e.(rounded up) =</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-4 font-bold">{formatCurrencyInt(roundedCommutatedValue)}</div>
                    </div>
                  </div>
                  <div className="mt-12 px-8"><div>Pension Clerk</div></div>
                  <div className="mt-8 px-8"><div>Chief Accountant</div></div>
                  <div className="mt-8 flex justify-end px-8"><div>Chief Executive Officer, Pune</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPensionReportPreview && pensionReport && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white w-[800px] max-w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-300">
              <h2 className="text-lg font-semibold text-gray-800">Provisional Gratuity</h2>
              <div className="flex gap-3 items-center">
                <button
                  onClick={handleDownloadPensionReportPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
                >
                  <Download size={16} />
                  Download PDF
                </button>
                <button
                  onClick={() => setShowPensionReportPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded text-gray-500"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto flex-1 bg-white">
              <div ref={printRefPensionReport} className="w-full text-black bg-white p-8 pt-1 font-serif text-[12px] leading-relaxed">
                <style>{`
                  .provisional-gratuity-row {
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                  }
                `}</style>
                {/* Header */}
                <div className="text-center mb-4 provisional-gratuity-row">
                  <div className="font-bold text-lg mb-1">PUNE CANTONMENT BOARD</div>
                  <div className="font-bold text-sm mb-1">GOLIBAR MAIDAN, PUNE - 1</div>
                  <div className="font-bold text-base uppercase mt-1">PROVISIONAL GRATUITY</div>
                </div>

                {/* Main Gratuity Section */}
                <div className="space-y-3 mb-8">
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8 font-bold">1</div>
                    <div className="w-48 font-bold">NAME</div>
                    <div className="w-4 text-center">:</div>
                    <div className="flex-1 font-bold uppercase">{pensionReport.employee_name}</div>
                  </div>
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8 font-bold">2</div>
                    <div className="w-48 font-bold">Designation</div>
                    <div className="w-4 text-center">:</div>
                    <div className="flex-1 uppercase">{pensionReport.designation}</div>
                  </div>
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8 font-bold">3</div>
                    <div className="w-48 font-bold">{pensionReport.date_label}</div>
                    <div className="w-4 text-center">:</div>
                    <div className="flex-1">{formatDateDot(new Date(pensionReport.date_value))}</div>
                  </div>
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8 font-bold">4</div>
                    <div className="w-48 font-bold">Qualifying Service</div>
                    <div className="w-4 text-center">:</div>
                    <div className="flex-1">{pensionReport.qualifying_service_formatted}</div>
                  </div>
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8"></div>
                    <div className="w-48 font-bold">Last pay drawn</div>
                  </div>
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8 font-bold">5</div>
                    <div className="w-48 font-bold">(emoluments)</div>
                    <div className="w-4 text-center">:</div>
                    <div className="flex-1">{pensionReport.last_pay_drawn}</div>
                  </div>
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8 font-bold">6</div>
                    <div className="w-48 font-bold">Average Emolument</div>
                    <div className="w-4 text-center">:</div>
                    <div className="flex-1">Rs. {pensionReport.average_emolument}/-</div>
                  </div>
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8"></div>
                    <div className="w-48 font-bold">Gratuity eligible under</div>
                    <div className="flex-1 ml-4">
                      {pensionReport.average_emolument} + {pensionReport.da_rate}% DA {pensionReport.da_value} =
                    </div>
                  </div>
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8 font-bold">7</div>
                    <div className="w-48 font-bold">Rule 50</div>
                    <div className="w-4 text-center">:</div>
                    <div className="flex-1">
                      <span className="font-bold">{pensionReport.rule50_amount}</span>
                    </div>
                  </div>
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8 font-bold">8</div>
                    <div className="w-48 font-bold">Three quarter of gratuity</div>
                    <div className="w-4 text-center">:</div>
                    <div className="flex-1">
                      {pensionReport.report_variant === 1 ? (
                        <>
                          Slab-based Death Gratuity calculation<br />
                          Rs. {formatCurrencyInt(pensionReport.gratuity_amount)}/-
                        </>
                      ) : (
                        <>
                          {pensionReport.rule50_amount} × {pensionReport.qualifying_service_years} × 2 / 4<br />
                          Rs. {formatCurrencyInt(pensionReport.gratuity_amount)}/-
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8"></div>
                    <div className="w-48 font-normal">Less 10 percent family pension deduction:</div>
                    <div className="w-4 text-center"></div>
                    <div className="flex-1">Rs. {formatCurrencyInt(pensionReport.family_pension_deduction)}/-</div>
                  </div>
                  <div className="flex items-start provisional-gratuity-row">
                    <div className="w-8 font-bold">9</div>
                    <div className="w-48 font-bold">Provisional Gratuity</div>
                    <div className="w-4 text-center font-bold">:</div>

                  </div>
                  <div className="flex items-start provisional-gratuity-row">

                    <div className="w-8 font-bold ">10</div>
                    <div className="w-48 font-bold">payable</div>
                    <div className="w-4 text-center"></div>
                    <div className="flex-1 font-bold">Rs. {formatCurrencyInt(pensionReport.provisional_gratuity)}/-</div>
                    <div className="flex-1"></div>
                  </div>
                </div>

                {/* Signature Section */}
                <div className="flex justify-between items-end mb-4 provisional-gratuity-row">
                  <div className="font-bold">Chief Accountant</div>
                </div>

                <div className="flex justify-end items-end mb-2 provisional-gratuity-row">
                  <div className="text-center">
                    <div className="font-bold mb-1">CHIEF EXECUTIVE OFFICER,PUNE</div>
                  </div>
                </div>

                {/* Pension Serial Number */}
                <div className="flex justify-between items-end mb-4 provisional-gratuity-row">
                  <div className="text-sm">
                    Pension Sl. No. ________________
                  </div>
                  <div className="text-sm font-semibold">Pune Cantonment Board</div>
                </div>

                {/* Pension Calculation Section */}
                <div className="text-center mt-6 mb-4 provisional-gratuity-row">
                  <div className="font-bold text-base uppercase">PENSION CALCULATION</div>
                </div>

                <div className="space-y-3">
                  {pensionReport.report_variant === 1 ? (
                    <div className="text-center py-8 text-gray-400 italic provisional-gratuity-row">
                      [ Pension Calculation Section remains blank for Expired / Death / Absconding cases ]
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start provisional-gratuity-row">
                        <div className="w-48 font-bold">Name</div>
                        <div className="w-4 text-center">:</div>
                        <div className="flex-1 font-semibold uppercase">{pensionReport.employee_name}</div>
                      </div>
                      <div className="flex items-start provisional-gratuity-row">
                        <div className="w-48 font-bold">Designation</div>
                        <div className="w-4 text-center">:</div>
                        <div className="flex-1 uppercase">{pensionReport.designation}</div>
                      </div>
                      <div className="flex items-start provisional-gratuity-row">
                        <div className="w-48 font-bold">Qualifying Service (In six</div>
                        <div className="w-4 text-center">:</div>
                        <div className="flex-1"></div>
                      </div>
                      <div className="flex items-start provisional-gratuity-row">
                        <div className="w-48 font-bold">monthly Periods)</div>
                        <div className="w-4 text-center"></div>
                        <div className="flex-1 font-bold">{pensionReport.qualifying_service_formatted}</div>
                      </div>
                      <div className="flex items-start provisional-gratuity-row">
                        <div className="w-48 font-bold">Average Emolument</div>
                        <div className="w-4 text-center">:</div>
                        <div className="flex-1">Rs. {pensionReport.average_emolument}/-</div>
                      </div>
                      <div className="flex items-start pt-4 provisional-gratuity-row">
                        <div className="w-48 font-bold ">Pension</div>
                        <div className="w-4 text-center font-bold text-lg">:</div>
                        <div className="flex-1">
                          <div className="font-bold mb-1">Pension for {pensionReport.qualifying_service_formatted} is</div>
                          <div className="font-bold ">Rs. {formatCurrencyInt(pensionReport.pension)}/- p.m.</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Final Authority Signature */}
                <div className="flex justify-end mt-12 provisional-gratuity-row">
                  <div className="text-center">
                    <div className="font-bold">CHIEF EXECUTIVE OFFICER,PUNE</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Family Pension Card */}
      <Employee360FamilyPensionCard employeeId={employeeId} profile={profile} loading={loading} />

      {/* Pension Scroll Card */}
      <Employee360PensionScrollCard employeeId={employeeId} profile={profile} loading={loading} />

      {/* Certificate Card */}
      <Employee360CertificateCard employeeId={employeeId} profile={profile} loading={loading} />

      {/* Official Note Card */}
      <Employee360OfficialNoteCard employeeId={employeeId} profile={profile} pensionReport={pensionReport} loading={loading} />

      {/* Pension Application Form Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
            <FileText size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Pension Application Form</h3>
            <p className="text-sm text-gray-500">Generate, preview and download the official 9-page pension application form.</p>
          </div>
        </div>
        <button
          onClick={() => setShowPensionAppForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
        >
          <Eye size={16} /> Preview & Download
        </button>
      </div>

      {showPensionAppForm && (
        <Employee360PensionApplicationForm
          employeeId={employeeId}
          onClose={() => setShowPensionAppForm(false)}
        />
      )}
    </div>
  );
};

export default Employee360PensionDocs;
